<template>
    <div class="bt-card">
        <div v-if="sleepPoints.length > 0" class="chart-wrap">
            <canvas ref="canvasRef"></canvas>
        </div>
        <div v-else>No data found for the selected date range.</div>

        <div v-if="sleepPoints.length > 0" class="summary">
            <div class="pill-accent"></div>

            <div class="summary-item">
                <div class="k">Bed Time Variability</div>
                <div class="v">{{ chartSummary.bedTimeVariability }}</div>
            </div>

            <div class="summary-item">
                <div class="k">Wake Time Variability</div>
                <div class="v">{{ chartSummary.wakeTimeVariability }}</div>
            </div>

            <div class="summary-item">
                <div class="k">Bed Time Range</div>
                <div class="v">{{ chartSummary.bedTimeRange }}</div>
            </div>

            <div class="summary-item">
                <div class="k">Wake Time Range</div>
                <div class="v">{{ chartSummary.wakeTimeRange }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend, } from "chart.js";
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

function fmtHHMM(totalMinutes) {
    // totalMinutes can exceed 1440; render modulo 24h
    let m = Math.round(Number(totalMinutes) || 0);
    m = ((m % 1440) + 1440) % 1440;
    const hh = Math.floor(m / 60);
    const mm = m % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function parseHHMMToMin(hhmm) {
    const [h, m] = String(hhmm).split(":").map((x) => parseInt(x, 10));
    return (h || 0) * 60 + (m || 0);
}

/**
 * Normalize a time into the axis timeline that starts at yMin.
 * If a time is earlier than yMin, treat it as next day.
 */
function normalizeToAxis(timeMin, yMin) {
    return timeMin < yMin ? timeMin + 1440 : timeMin;
}

export default {
    name: "BedWakeTimeRangeChart",
    props: {
        sleepPoints: {
            type: Array,
            default: () => [],
            required: true,
        },
    },

    data() {
        return {
            chart: null,
            summary: {
                bedTimeVariability: "",
                wakeTimeVariability: "",
                bedTimeRange: "",
                wakeTimeRange: "",
            },
            barColor: "#a100ff",
            gridColor: "rgba(255,255,255,0.08)",
            tickColor: "rgba(255,255,255,0.65)",
            yMinHHMM: "13:00",
            yMaxHHMM: "12:00"
        };
    },

    mounted() {
    this.$nextTick(() => {
      this.renderChart();
    });
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.onResize);
        this.destroyChart();
    },

    computed: {
        chartSummary() {
            if (this.sleepPoints && this.sleepPoints.length > 0) {
                return this.generateSummaryFromPoints(this.sleepPoints);
            }
            return this.summary;
        },
    },

    watch: {
        sleepPoints: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.renderChart();
                });
            },
        },
    },

    methods: {
        generateSummaryFromPoints(points) {
            if (!points || points.length === 0) {
                return {
                    bedTimeVariability: "N/A",
                    wakeTimeVariability: "N/A",
                    bedTimeRange: "N/A",
                    wakeTimeRange: "N/A",
                };
            }

            const bedTimes = points.map((p) => parseHHMMToMin(p.bedTime));
            const wakeTimes = points.map((p) => parseHHMMToMin(p.wakeTime));

            // Handle overnight times for variability calculation
            // Center times around a pivot (e.g., 3 AM) to handle overnight values correctly
            const pivot = 3 * 60; // 3 AM in minutes
            const adjust = (t) => (t < pivot ? t + 1440 : t);
            const adjustedBedTimes = bedTimes.map(adjust);
            const adjustedWakeTimes = wakeTimes.map(adjust);

            const calculateVariability = (times) => {
                if (times.length < 2) return "0m";
                const mean = times.reduce((a, b) => a + b, 0) / times.length;
                const stdDev = Math.sqrt(
                    times.map((t) => (t - mean) ** 2).reduce((a, b) => a + b, 0) /
                    times.length
                );
                return `${Math.round(stdDev)}m`;
            };

            const bedTimeVariability = calculateVariability(adjustedBedTimes);
            const wakeTimeVariability = calculateVariability(adjustedWakeTimes);

            const minBedTime = Math.min(...bedTimes);
            const maxBedTime = Math.max(...bedTimes);
            const minWakeTime = Math.min(...wakeTimes);
            const maxWakeTime = Math.max(...wakeTimes);

            return {
                bedTimeVariability,
                wakeTimeVariability,
                bedTimeRange: `${fmtHHMM(minBedTime)} - ${fmtHHMM(maxBedTime)}`,
                wakeTimeRange: `${fmtHHMM(minWakeTime)} - ${fmtHHMM(maxWakeTime)}`,
            };
        },

        onResize() {
            if (this.chart) this.chart.resize();
        },

        destroyChart() {
            if (this.chart) {
                this.chart.destroy();
                this.chart = null;
            }
        },
        makeRangeLabelsPlugin() {
            return {
                id: "rangeLabels",
                afterDatasetsDraw(chart) {
                    const dsIndex = 0;
                    const meta = chart.getDatasetMeta(dsIndex);
                    if (!meta?.data?.length) return;

                    const { ctx } = chart;
                    ctx.save();
                    ctx.font = "12px sans-serif";
                    ctx.textAlign = "center";

                    meta.data.forEach((bar, i) => {
                        const raw = chart.data.datasets[dsIndex].data[i]; // [start,end]
                        if (!Array.isArray(raw) || raw.length !== 2) return;

                        const start = raw[0]; // bed
                        const end = raw[1];   // wake (possibly next day)

                        // wake label above top
                        ctx.fillStyle = "rgba(255,255,255,0.75)";
                        ctx.textBaseline = "bottom";
                        ctx.fillText(fmtHHMM(end), bar.x, bar.y - 8);

                        // bed label below bottom
                        ctx.fillStyle = "rgba(255,255,255,0.75)";
                        ctx.textBaseline = "top";
                        ctx.fillText(fmtHHMM(start), bar.x, bar.base + 10);
                    });

                    ctx.restore();
                },
            };
        },

        renderChart() {
            this.destroyChart();

            if (!this.sleepPoints || this.sleepPoints.length === 0) {
                return;
            }

            const ctx = this.$refs.canvasRef?.getContext("2d");
            if (!ctx) return;

            const yMin = parseHHMMToMin(this.yMinHHMM);
            let yMax = parseHHMMToMin(this.yMaxHHMM);
            yMax = normalizeToAxis(yMax, yMin);
            if (yMax <= yMin) yMax += 1440; // treat as next day

            const labels = this.sleepPoints.map((p) => [p.sleepDate, p.sleepDay]); // 2-line x ticks
            // Convert each point into a "floating bar": [bedMinutes, wakeMinutesAdjusted]
            const ranges = this.sleepPoints.map((p) => { 
                const bed0 = parseHHMMToMin(p.bedTime);
                const wake0 = parseHHMMToMin(p.wakeTime);

                // Align both to the axis timeline (relative to yMin)
                let bed = normalizeToAxis(bed0, yMin);
                let wake = normalizeToAxis(wake0, yMin);

                // Ensure wake is after bed (next day if needed)
                if (wake <= bed) wake += 1440;

                return [bed, wake];
            });

            this.chart = new Chart(ctx, {
                type: "bar",
                data: {
                    labels,
                    datasets: [
                        {
                            label: "Bed / Wake Time",
                            data: ranges,
                            backgroundColor: this.barColor,
                            borderRadius: {
                                topLeft: 10,
                                topRight: 10,
                                bottomLeft: 10,
                                bottomRight: 10
                            },
                            borderSkipped: false,
                            borderWidth: 0,
                            barPercentage: 0.12, //bar width
                            categoryPercentage: 0.75
                        }
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 12, right: 18, bottom: 0, left: 10 } },
                    plugins: {
                        legend: {
                            position: "bottom",
                            labels: {
                                color: this.tickColor,
                                boxWidth: 15,
                                boxHeight: 15,
                            },
                        },
                        tooltip: {
                            callbacks: {
                                title: (items) => {
                                    const lbl = items?.[0]?.label;
                                    if (Array.isArray(lbl)) return `${lbl[0]} ${lbl[1]}`;
                                    return String(lbl || "");
                                },
                                label: (ctx) => {
                                    const [bed, wake] = ctx.raw || [];
                                    return `Bed: ${fmtHHMM(bed)}  •  Wake: ${fmtHHMM(wake)}`;
                                },
                            },
                        },
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false,
                                drawBorder: false
                            },
                            ticks: {
                                color: this.tickColor,
                                font: { size: 11 }
                            }
                        },
                        y: {
                            min: yMin,
                            max: yMax,
                            grid: {
                                display: false,
                                drawBorder: false
                            },
                            ticks: {
                                color: this.tickColor,
                                stepSize: 60,
                                callback: (v) => fmtHHMM(v)
                            },
                            title: {
                                display: true,
                                text: "BED / WAKE TIME",
                                color: this.tickColor,
                                font: { size: 12, weight: "600" }
                            }
                        }
                    },
                    elements: {
                        bar: {
                            borderWidth: 0
                        }
                    }
                },
                plugins: [this.makeRangeLabelsPlugin()],
            });
        },
    },
};
</script>

<style scoped>
.bt-card {
    width: 100%;
    background: #233334;
    border-radius: 12px;
    padding: 14px 14px 10px;
    box-sizing: border-box;
}

.chart-wrap {
    height: 320px;
    /* close to screenshot aspect */
    width: 100%;
}

.summary {
    margin: 14px auto 0;
    max-width: 880px;
    width: 100%;
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 12px 14px;
    display: grid;
    grid-template-columns: 10px repeat(4, 1fr);
    gap: 14px;
    align-items: center;
    box-sizing: border-box;
}

.pill-accent {
    width: 6px;
    height: 100%;
    border-radius: 999px;
    background: #a100ff;
    opacity: 0.9;
    justify-self: start;
}

.summary-item .k {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 4px;
    white-space: nowrap;
}

.summary-item .v {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.92);
    font-weight: 600;
    white-space: nowrap;
}

@media (max-width: 900px) {
    .summary {
        grid-template-columns: 10px repeat(2, 1fr);
        row-gap: 10px;
    }
}
</style>
